// PublicPages.js
import React, { useState } from 'react';
import { contactAPI, faqAPI, techniqueAPI } from '../../services/api';
import { Button, FormField, Input, Textarea, Select } from '../../components/common';
import { toast } from 'react-toastify';
import { useSite } from '../../context/SiteContext';

export const ContactPage = () => {
  const { settings } = useSite();
  const [form, setForm]     = useState({ name:'', email:'', company:'', phone:'', subject:'', message:'' });
  const [loading, setLoading] = useState(false);
  const [sent, setSent]       = useState(false);

  const subjects = ['Demande de renseignements', 'Demande de devis', 'Question technique', 'Suivi de commande', 'Réclamation', 'Autre'];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await contactAPI.submit(form);
      setSent(true);
      toast.success('Message envoyé !');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erreur lors de l\'envoi');
    } finally { setLoading(false); }
  };

  return (
    <div style={{paddingTop:'var(--header-h)',minHeight:'100vh'}}>
      <div className="container" style={{maxWidth:'1100px',padding:'var(--space-3xl) var(--space-lg)'}}>
        <div style={{textAlign:'center',marginBottom:'var(--space-2xl)'}}>
          <h1 style={{fontSize:'clamp(28px,4vw,44px)',fontWeight:800,marginBottom:'var(--space-sm)'}}>Contactez-nous</h1>
          <p style={{color:'var(--text-secondary)',fontSize:'17px'}}>Notre équipe répond à toutes vos demandes sous 24 à 48h.</p>
        </div>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1.6fr',gap:'var(--space-2xl)',alignItems:'start'}}>
          {/* Info */}
          <div style={{display:'flex',flexDirection:'column',gap:'var(--space-xl)'}}>
            {[
              { label:'Email', val: settings.site_email, href:`mailto:${settings.site_email}` },
              { label:'Téléphone', val: settings.site_phone, href:`tel:${settings.site_phone}` },
              { label:'Adresse', val: settings.site_address },
            ].map(item => item.val && (
              <div key={item.label} style={{display:'flex',gap:'var(--space-md)',alignItems:'flex-start'}}>
                <div>
                  <div style={{fontSize:12,fontWeight:700,textTransform:'uppercase',letterSpacing:'0.5px',color:'var(--text-muted)',marginBottom:4}}>{item.label}</div>
                  {item.href ? <a href={item.href} style={{fontSize:15,fontWeight:600,color:'var(--text-primary)'}}>{item.val}</a> : <p style={{fontSize:15,color:'var(--text-secondary)',lineHeight:1.5}}>{item.val}</p>}
                </div>
              </div>
            ))}
          </div>

          {/* Form */}
          {sent ? (
            <div style={{textAlign:'center',padding:'var(--space-2xl)',background:'var(--success-bg)',borderRadius:'var(--radius-xl)',border:'1px solid #a7f3d0'}}>
              <div style={{fontSize:48,marginBottom:'var(--space-md)'}}>✅</div>
              <h3 style={{fontSize:22,fontWeight:700,color:'#065f46',marginBottom:'var(--space-sm)'}}>Message envoyé !</h3>
              <p style={{color:'#047857'}}>Nous vous répondrons dans les plus brefs délais.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} style={{background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:'var(--radius-xl)',padding:'var(--space-xl)',display:'flex',flexDirection:'column',gap:'var(--space-md)'}}>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'var(--space-md)'}}>
                <FormField label="Nom / Prénom" required>
                  <Input required value={form.name} onChange={e=>setForm(p=>({...p,name:e.target.value}))} placeholder="Jean Dupont"/>
                </FormField>
                <FormField label="Email" required>
                  <Input type="email" required value={form.email} onChange={e=>setForm(p=>({...p,email:e.target.value}))} placeholder="email@entreprise.fr"/>
                </FormField>
                <FormField label="Entreprise">
                  <Input value={form.company} onChange={e=>setForm(p=>({...p,company:e.target.value}))} placeholder="Mon Entreprise"/>
                </FormField>
                <FormField label="Téléphone">
                  <Input type="tel" value={form.phone} onChange={e=>setForm(p=>({...p,phone:e.target.value}))} placeholder="+33 6 12 34 56 78"/>
                </FormField>
              </div>
              <FormField label="Sujet" required>
                <Select required value={form.subject} onChange={e=>setForm(p=>({...p,subject:e.target.value}))}>
                  <option value="">Choisir un sujet…</option>
                  {subjects.map(s=><option key={s} value={s}>{s}</option>)}
                </Select>
              </FormField>
              <FormField label="Message" required>
                <Textarea required rows={5} value={form.message} onChange={e=>setForm(p=>({...p,message:e.target.value}))} placeholder="Décrivez votre besoin…"/>
              </FormField>
              <Button type="submit" variant="primary" size="lg" loading={loading} fullWidth>Envoyer le message</Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

// FAQPage.js
export const FAQPage = () => {
  const [faqs, setFaqs]       = React.useState([]);
  const [open, setOpen]       = React.useState(null);
  const [loading, setLoading] = React.useState(true);


  React.useEffect(() => {
    faqAPI.list().then(({data})=>setFaqs(data.data||[])).catch(()=>{}).finally(()=>setLoading(false));
  }, []);

  const grouped = faqs.reduce((acc,f) => {
    if (!acc[f.category]) acc[f.category] = [];
    acc[f.category].push(f);
    return acc;
  }, {});

  const catLabels = { general:'Général', commande:'Commandes', fichiers:'Fichiers & Formats', production:'Production', studio:'Studio', livraison:'Livraison' };

  return (
    <div style={{paddingTop:'var(--header-h)',minHeight:'100vh',background:'var(--bg-page)'}}>
      <div style={{background:'var(--brand-primary)',color:'white',padding:'var(--space-2xl) 0'}}>
        <div className="container">
          <h1 style={{fontSize:'clamp(28px,4vw,44px)',fontWeight:800,color:'white',marginBottom:'var(--space-sm)'}}>Questions fréquentes</h1>
          <p style={{color:'rgba(255,255,255,.7)',fontSize:17}}>Retrouvez les réponses à toutes vos questions.</p>
        </div>
      </div>
      <div className="container" style={{maxWidth:820,padding:'var(--space-2xl) var(--space-lg)'}}>
        {loading ? <div className="page-loader"><div className="spinner spinner-lg"/></div> :
          Object.entries(grouped).map(([cat, items]) => (
            <div key={cat} style={{marginBottom:'var(--space-2xl)'}}>
              <h2 style={{fontSize:18,fontWeight:700,marginBottom:'var(--space-md)',color:'var(--text-secondary)',borderBottom:'1px solid var(--border)',paddingBottom:'var(--space-sm)'}}>{catLabels[cat]||cat}</h2>
              {items.map((faq) => (
                <div key={faq.id} style={{marginBottom:8,border:'1px solid var(--border)',borderRadius:'var(--radius-md)',overflow:'hidden',background:'var(--bg-card)'}}>
                  <button
                    style={{width:'100%',padding:'var(--space-md) var(--space-lg)',textAlign:'left',display:'flex',alignItems:'center',justifyContent:'space-between',gap:'var(--space-md)',fontWeight:600,fontSize:15,background:'transparent',color:'var(--text-primary)'}}
                    onClick={()=>setOpen(open===faq.id?null:faq.id)}
                  >
                    {faq.question}
                    <svg style={{flexShrink:0,transition:'transform 0.2s',transform:open===faq.id?'rotate(180deg)':'rotate(0)'}} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
                  </button>
                  {open===faq.id && (
                    <div style={{padding:'0 var(--space-lg) var(--space-md)',fontSize:14,color:'var(--text-secondary)',lineHeight:1.7,borderTop:'1px solid var(--border)'}}>{faq.answer}</div>
                  )}
                </div>
              ))}
            </div>
          ))
        }
      </div>
    </div>
  );
};

// LegalPages.js (Mentions légales, CGV, Protection données)
export const LegalPage = ({ settingKey, title }) => {
  const [content, setContent] = React.useState('');
  const { settings }          = useSite();

  React.useEffect(() => {
    setContent(settings[settingKey] || '');
  }, [settings, settingKey]);

  return (
    <div style={{paddingTop:'var(--header-h)',minHeight:'100vh'}}>
      <div style={{background:'var(--brand-primary)',padding:'var(--space-2xl) 0 var(--space-xl)'}}>
        <div className="container"><h1 style={{color:'white',fontSize:'clamp(24px,3vw,38px)',fontWeight:800}}>{title}</h1></div>
      </div>
      <div className="container" style={{maxWidth:860,padding:'var(--space-2xl) var(--space-lg)'}}>
        {content
          ? <div style={{lineHeight:1.8,color:'var(--text-secondary)',fontSize:15}} dangerouslySetInnerHTML={{__html:content}}/>
          : (
            <div style={{color:'var(--text-muted)',fontSize:15,lineHeight:1.8}}>
              <p>Ce contenu est en cours de rédaction. Revenez bientôt.</p>
              {settingKey === 'legal_content' && (
                <div style={{marginTop:'var(--space-xl)'}}>
                  <h2 style={{fontSize:18,fontWeight:700,marginBottom:'var(--space-md)'}}>Informations légales</h2>
                  <p><strong>Raison sociale :</strong> {settings.legal_company}</p>
                  <p><strong>SIRET :</strong> {settings.legal_siret}</p>
                  <p><strong>RCS :</strong> {settings.legal_rcs}</p>
                  <p><strong>Capital social :</strong> {settings.legal_capital}</p>
                  <p><strong>Directeur de publication :</strong> {settings.legal_director}</p>
                  <p><strong>Hébergeur :</strong> {settings.legal_host}</p>
                  <p><strong>Contact :</strong> {settings.site_email}</p>
                </div>
              )}
            </div>
          )
        }
      </div>
    </div>
  );
};

// TechniquesPage.js
export const TechniquesPage = () => {
  const [techniques,  setTechniques]  = React.useState([]);
  const [active,      setActive]      = React.useState(null);

  React.useEffect(() => {
    techniqueAPI.list()
      .then(({ data }) => {
        const list = data.data || [];
        setTechniques(list);
        if (list.length) setActive(list[0].id);
      })
      .catch(() => {});
  }, []);

  // Infos enrichies par slug
  const meta = {
    serigraphie:     { emoji: '🖨', ideal: 'Grandes séries • Couleurs vives • Coût réduit à volume' },
    broderie:        { emoji: '🧵', ideal: 'Rendu premium • Logos d entreprise • Durable lavage' },
    'transfert-dtf': { emoji: '📸', ideal: 'Photos & dégradés • Petites séries • Couleurs illimitées' },
    'impression-dtg':{ emoji: '🎨', ideal: 'Impression directe • Détails fins • Pièces uniques' },
    flocage:         { emoji: '✨', ideal: 'Aspect velours • Chiffres & lettres • Sportswear' },
    vinyle:          { emoji: '💠', ideal: 'Tracés nets • Petits logos • Finition brillante ou mat' },
  };
  const ACCENT = 'var(--brand-accent)';

  const currentTech = techniques.find(t => t.id === active);
  const m = currentTech ? { ...(meta[currentTech.slug] || { emoji: '✅', ideal: '' }), color: ACCENT } : null;

  return (
    <div style={{ paddingTop: 'var(--header-h)', minHeight: '100vh', background: 'var(--bg-page)' }}>

      {/* Hero */}
      <div style={{ background: 'linear-gradient(135deg, var(--brand-primary) 0%, #e63946 100%)', padding: 'var(--space-2xl) 0', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, opacity: .06, backgroundImage: 'radial-gradient(circle at 20% 50%, white 1px, transparent 1px)', backgroundSize: '32px 32px' }} />
        <div className="container" style={{ position: 'relative' }}>
          <div style={{ display: 'inline-block', background: 'rgba(255,255,255,.15)', color: 'white', fontSize: 12, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', padding: '4px 14px', borderRadius: 99, marginBottom: 16 }}>
            Savoir-faire
          </div>
          <h1 style={{ color: 'white', fontSize: 'clamp(28px,4vw,48px)', fontWeight: 800, marginBottom: 12, lineHeight: 1.15 }}>
            Nos Techniques de Marquage
          </h1>
          <p style={{ color: 'rgba(255,255,255,.8)', fontSize: 18, maxWidth: 560, lineHeight: 1.6 }}>
            Chaque projet est unique. Découvrez nos techniques et choisissez celle qui correspond le mieux à vos besoins.
          </p>
        </div>
      </div>

      <div className="container" style={{ padding: 'var(--space-2xl) var(--space-lg)' }}>

        {/* Onglets de navigation */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 'var(--space-xl)' }}>
          {techniques.map(t => {
            const isActive = String(active) === String(t.id);
            return (
              <button
                key={t.id}
                onClick={() => setActive(t.id)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '10px 20px',
                  background:   isActive ? 'var(--brand-accent)' : 'var(--bg-card)',
                  border:       isActive ? '2px solid var(--brand-accent)' : '2px solid var(--border)',
                  borderRadius: 'var(--radius-full)',
                  cursor:       'pointer',
                  fontWeight:   isActive ? 700 : 500,
                  fontSize:     14,
                  color:        isActive ? 'white' : 'var(--text-secondary)',
                  transition:   'all .2s',
                  whiteSpace:   'nowrap',
                }}
              >
                {t.name}
              </button>
            );
          })}
        </div>

        {/* Détail technique active */}
        {currentTech && m && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-xl)', alignItems: 'start' }}>

            {/* Colonne gauche — image + visuel */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {/* Image ou placeholder illustré */}
              <div style={{
                aspectRatio: '4/3',
                borderRadius: 'var(--radius-xl)',
                overflow: 'hidden',
                background: `linear-gradient(135deg, ${m.color}18 0%, ${m.color}35 100%)`,
                border: `2px solid ${m.color}30`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                position: 'relative',
              }}>
                {currentTech.image ? (
                  <img
                    src={currentTech.image}
                    alt={currentTech.name}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                ) : (
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 80, marginBottom: 12, filter: 'drop-shadow(0 4px 12px rgba(0,0,0,.15))' }}>{m.emoji}</div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: m.color, opacity: .8 }}>{currentTech.name}</div>
                  </div>
                )}
              </div>

              {/* Cas d'usage idéal */}
              {m.ideal && (
                <div style={{
                  background: `${m.color}0d`,
                  border: `1px solid ${m.color}30`,
                  borderRadius: 'var(--radius-lg)',
                  padding: '14px 18px',
                }}>
                  <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: .8, color: m.color, marginBottom: 8 }}>
                    Idéal pour
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {m.ideal.split(' • ').map((tag, i) => (
                      <span key={i} style={{
                        fontSize: 12, fontWeight: 600,
                        background: 'white', color: 'var(--text-secondary)',
                        padding: '3px 10px', borderRadius: 99,
                        border: `1px solid ${m.color}40`,
                      }}>{tag}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Colonne droite — infos */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

              <div>
                <h2 style={{ fontSize: 'clamp(22px,3vw,32px)', fontWeight: 800, marginBottom: 8, color: 'var(--text-primary)' }}>
                  {currentTech.name}
                </h2>
                {currentTech.description && (
                  <p style={{ fontSize: 16, lineHeight: 1.8, color: 'var(--text-secondary)' }}>
                    {currentTech.description}
                  </p>
                )}
              </div>

              {/* Tarifs */}
              <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
                <div style={{ padding: '12px 18px', background: m.color, color: 'white', fontWeight: 700, fontSize: 13, letterSpacing: .5 }}>
                  Tarifs
                </div>
                <div style={{ padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 14 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 2 }}>Frais de mise en route</div>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic' }}>Préparation des fichiers, matrices</div>
                    </div>
                    <div style={{ fontSize: 22, fontWeight: 800, color: m.color, whiteSpace: 'nowrap' }}>
                      {parseFloat(currentTech.price_setup) === 0 ? 'Gratuit' : `${parseFloat(currentTech.price_setup).toFixed(2)} €`}
                    </div>
                  </div>
                  <div style={{ borderTop: '1px solid var(--border)' }} />
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 2 }}>Prix par pièce</div>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic' }}>En supplément du prix du vêtement</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>à partir de</div>
                      <div style={{ fontSize: 22, fontWeight: 800, color: m.color, whiteSpace: 'nowrap' }}>
                        + {parseFloat(currentTech.price_per_unit).toFixed(2)} €
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Caractéristiques */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                {[
                  { label: 'Quantité minimum', value: currentTech.min_quantity > 1 ? `${currentTech.min_quantity} pièces` : 'Dès 1 pièce'},
                  { label: 'Couleurs max', value: currentTech.max_colors ? `${currentTech.max_colors} couleurs` : 'Illimitées'},
                ].map(({ label, value, ok }) => (
                  <div key={label} style={{
                    background: ok === false ? '#fff5f5' : 'var(--bg-card)',
                    border: `1px solid ${ok === false ? '#fecaca' : 'var(--border)'}`,
                    borderRadius: 'var(--radius-md)',
                    padding: '12px 14px',
                  }}>
                    <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: .5, color: 'var(--text-muted)', marginBottom: 4 }}>
                      {label}
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: ok === false ? '#dc2626' : ok === true ? '#16a34a' : 'var(--text-primary)' }}>
                      {ok === true ? '✓ ' : ok === false ? '✗ ' : ''}{value}
                    </div>
                  </div>
                ))}
              </div>

              {/* CTA */}
              <a
                href="/catalogue"
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                  padding: '14px 24px',
                  background: m.color,
                  color: 'white',
                  fontWeight: 700, fontSize: 15,
                  borderRadius: 'var(--radius-lg)',
                  textDecoration: 'none',
                  transition: 'opacity .2s',
                }}
                onMouseEnter={e => e.currentTarget.style.opacity = '.85'}
                onMouseLeave={e => e.currentTarget.style.opacity = '1'}
              >
                Personnaliser un vêtement avec cette technique →
              </a>
            </div>
          </div>
        )}

        {/* Tableau comparatif */}
        {techniques.length > 1 && (
          <div style={{ marginTop: 'var(--space-2xl)' }}>
            <h2 style={{ fontSize: 22, fontWeight: 800, marginBottom: 'var(--space-md)', textAlign: 'center' }}>
              Comparatif des techniques
            </h2>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: 'var(--brand-primary)', color: 'white' }}>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 700 }}>Technique</th>
                    <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 700 }}>Mise en route</th>
                    <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 700 }}>À partir de / pièce</th>
                    <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 700 }}>Qté min.</th>
                  </tr>
                </thead>
                <tbody>
                  {techniques.map((t, i) => {
                    const tm = { ...(meta[t.slug] || { emoji: '✅' }), color: 'var(--brand-accent)' };
                    return (
                      <tr
                        key={t.id}
                        onClick={() => { setActive(t.id); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                        style={{
                          background: String(active) === String(t.id) ? 'rgba(230,57,70,.07)' : i % 2 === 0 ? 'var(--bg-card)' : 'var(--bg-page)',
                          cursor: 'pointer',
                          borderLeft: String(active) === String(t.id) ? '4px solid var(--brand-accent)' : '4px solid transparent',
                          transition: 'all .15s',
                        }}
                      >
                        <td style={{ padding: '11px 16px', fontWeight: String(active) === String(t.id) ? 700 : 500 }}>
                          {t.name}
                        </td>
                        <td style={{ padding: '11px 16px', textAlign: 'center', color: parseFloat(t.price_setup) === 0 ? '#16a34a' : 'var(--text-primary)', fontWeight: 600 }}>
                          {parseFloat(t.price_setup) === 0 ? 'Gratuit' : `${parseFloat(t.price_setup).toFixed(2)} €`}
                        </td>
                        <td style={{ padding: '11px 16px', textAlign: 'center', fontWeight: 700, color: tm.color }}>
                          + {parseFloat(t.price_per_unit).toFixed(2)} €
                        </td>
                        <td style={{ padding: '11px 16px', textAlign: 'center' }}>
                          {t.min_quantity > 1 ? `${t.min_quantity} pcs` : '1 pcs'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};